import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Button, Space, Tag, Typography, Select, Popconfirm, message } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  PrinterOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import intercompanyService, { type IcTransaction } from '@/services/intercompany.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { printIcTransaction } from '@/utils/intercompanyPrint';

const { Title } = Typography;

const statusColor: Record<string, string> = {
  Draft: 'default',
  Submitted: 'processing',
  Approved: 'success',
  Posted: 'blue',
  Cancelled: 'error',
};

const IntercompanyTransactionsPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { formatCurrency } = useCurrency();
  const [status, setStatus] = useState<string>('');

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      Draft: t('statusDraft'),
      Submitted: t('statusSubmitted'),
      Approved: t('statusApproved'),
      Posted: t('statusPosted'),
      Cancelled: t('statusCancelled'),
    };
    return map[s] ?? s;
  };

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['intercompany-transactions', status],
    queryFn: () => intercompanyService.listTransactions({ status: status || undefined }),
  });

  const { data: affiliates = [] } = useQuery({
    queryKey: ['intercompany-affiliates'],
    queryFn: () => intercompanyService.listAffiliates(),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => intercompanyService.deleteTransaction(id),
    onSuccess: () => {
      message.success(t('deleted'));
      qc.invalidateQueries({ queryKey: ['intercompany-transactions'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const affiliateName = (id: string) => {
    const a = affiliates.find((x) => x.company_id === id);
    if (a) return `${a.company_code} — ${a.company_name}`;
    return t('affiliate');
  };

  const handlePrint = async (row: IcTransaction) => {
    try {
      const full = row.lines?.length ? row : await intercompanyService.getTransaction(row.id);
      printIcTransaction(full, {
        formatCurrency,
        buyerName: affiliateName(full.buyer_company_id),
        sellerName: t('currentCompany'),
      });
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('printFailed'));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Title level={4} style={{ margin: 0 }}>
          {t('intercompanyTransactions')}
        </Title>
        <Space wrap>
          <Button onClick={() => navigate('/fin/intercompany/affiliates')}>{t('affiliates')}</Button>
          <Button onClick={() => navigate('/fin/intercompany/settlements')}>{t('settlements')}</Button>
          <Button onClick={() => navigate('/fin/reports/intercompany-balance')}>{t('icBalanceShort')}</Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/fin/intercompany/transactions/new')}
          >
            {t('newIcDocument')}
          </Button>
        </Space>
      </Space>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder={t('filterByStatus')}
          style={{ width: 200 }}
          value={status || undefined}
          onChange={(v) => setStatus(v ?? '')}
          options={['Draft', 'Submitted', 'Approved', 'Posted', 'Cancelled'].map((s) => ({
            value: s,
            label: statusLabel(s),
          }))}
        />
      </Card>

      <Table
        loading={isLoading}
        rowKey="id"
        dataSource={list}
        scroll={{ x: 1200 }}
        columns={[
          {
            title: t('icNo'),
            dataIndex: 'ic_number',
            render: (v, r) => (
              <a onClick={() => navigate(`/fin/intercompany/transactions/${r.id}`)}>{v}</a>
            ),
          },
          {
            title: t('date'),
            dataIndex: 'transaction_date',
            render: (d) => (d ? dayjs(d).format('DD MMM YYYY') : '—'),
          },
          {
            title: t('buyer'),
            dataIndex: 'buyer_company_id',
            render: (id) => affiliateName(id),
          },
          {
            title: t('type'),
            dataIndex: 'transaction_type',
            render: (v) => v?.replace(/([A-Z])/g, ' $1').trim(),
          },
          {
            title: tc('status'),
            dataIndex: 'status',
            render: (s) => <Tag color={statusColor[s] ?? 'default'}>{statusLabel(s)}</Tag>,
          },
          {
            title: t('total'),
            dataIndex: 'grand_total',
            align: 'right',
            render: (v, r) => formatCurrency(Number(v), r.currency),
          },
          {
            title: tc('actions'),
            key: 'actions',
            width: 260,
            render: (_, r) => (
              <Space size={0} wrap>
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => navigate(`/fin/intercompany/transactions/${r.id}`)}
                >
                  {tc('view')}
                </Button>
                {r.status === 'Draft' && (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/fin/intercompany/transactions/${r.id}/edit`)}
                  >
                    {tc('edit')}
                  </Button>
                )}
                <Button
                  type="link"
                  size="small"
                  icon={<PrinterOutlined />}
                  onClick={() => handlePrint(r)}
                >
                  {t('print')}
                </Button>
                {(r.status === 'Draft' || r.status === 'Cancelled') && (
                  <Popconfirm
                    title={t('deleteIcDocumentConfirm')}
                    onConfirm={() => deleteMut.mutate(r.id)}
                  >
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                      {tc('delete')}
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />
    </div>
  );
};

export default IntercompanyTransactionsPage;
